/**
 * G13 / V34: project changelog, "Since you last visited" and badges. Everything is derived on the
 * user's daemon from verified events and decrypted here; each row opens its evidence.
 *
 * Exported for the Lanes project tab to mount: `SinceLastVisit`, `SinceBadge`,
 * `ProjectChangelog`, `ChangelogList`.
 */
import { Link, useSearch } from '@tanstack/react-router'
import {
  CHANGELOG_KIND_LABEL,
  CHANGELOG_KINDS,
  type ChangelogItem,
  type ChangelogKind,
  type EvidenceRef,
  itemsSince,
} from '@viko/protocol'
import { Button, Chip } from '@viko/transcript-ui'
import {
  CircleAlert,
  CircleCheck,
  GitCommitHorizontal,
  GitMerge,
  Hammer,
  OctagonX,
  Rocket,
  Scale,
} from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import {
  digestCounts,
  evidenceTarget,
  sinceCounts,
  sinceLineState,
  useChangelogs,
  useLastVisits,
  useMarkVisited,
  visitTracker,
} from '../lib/digests.js'
import { Ago, Empty, SkeletonRows } from './ui.js'

const ICON: Record<ChangelogKind, ReactNode> = {
  release: <Rocket size={14} aria-hidden="true" />,
  landed: <GitMerge size={14} aria-hidden="true" />,
  build: <Hammer size={14} aria-hidden="true" />,
  decision: <Scale size={14} aria-hidden="true" />,
  'task-done': <CircleCheck size={14} aria-hidden="true" />,
  'task-blocked': <CircleAlert size={14} aria-hidden="true" />,
  failure: <OctagonX size={14} aria-hidden="true" />,
}
const TONE: Record<ChangelogKind, string> = {
  release: 'text-status-ok',
  landed: 'text-status-ok',
  build: 'text-text-2',
  decision: 'text-accent-text',
  'task-done': 'text-status-ok',
  'task-blocked': 'text-status-wait',
  failure: 'text-status-fail',
}

function EvidenceChip({ e, item }: { e: EvidenceRef; item: ChangelogItem }) {
  const t = evidenceTarget(e, item)
  const label = e.label ?? (e.kind === 'commit' ? e.id.slice(0, 7) : e.kind)
  const content = (
    <Chip
      mono={e.kind === 'commit'}
      icon={e.kind === 'commit' ? <GitCommitHorizontal size={12} aria-hidden="true" /> : undefined}
      title={`Open ${e.kind} ${e.id}`}
    >
      {label}
    </Chip>
  )
  if (t.kind === 'url')
    return (
      <a href={t.url} target="_blank" rel="noreferrer noopener" className="no-underline">
        {content}
      </a>
    )
  return (
    <Link
      to={t.to as never}
      params={t.params as never}
      search={t.search as never}
      className="no-underline"
    >
      {content}
    </Link>
  )
}

export function ChangelogRow({ item }: { item: ChangelogItem }) {
  return (
    <li className="flex gap-2 py-2" data-testid="changelog-row">
      <span
        className={`${TONE[item.kind]} mt-0.5 shrink-0`}
        title={CHANGELOG_KIND_LABEL[item.kind]}
      >
        {ICON[item.kind]}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 text-[14px]">
          <span className="font-medium text-text-1">{item.title}</span>
          {item.count > 1 ? (
            <span className="text-text-3" title={`${item.count} occurrences`}>
              ×{item.count}
            </span>
          ) : null}
          <span className="text-[14px] text-text-3">
            <Ago ts={item.at} />
          </span>
        </div>
        {item.detail ? <p className="m-0 text-[14px] text-text-2">{item.detail}</p> : null}
        <div className="mt-1 flex flex-wrap gap-1" role="group" aria-label="Evidence">
          {item.evidence.map((e) => (
            <EvidenceChip key={`${e.kind}:${e.id}`} e={e} item={item} />
          ))}
        </div>
      </div>
    </li>
  )
}

/** Items grouped by kind (display order), each group with its exact count. */
export function ChangelogList({ items, limit }: { items: ChangelogItem[]; limit?: number }) {
  const [all, setAll] = useState(false)
  const shown = limit && !all ? items.slice(0, limit) : items
  const groups = CHANGELOG_KINDS.flatMap((k) => {
    const rows = shown.filter((i) => i.kind === k)
    return rows.length ? [{ kind: k, rows, total: items.filter((i) => i.kind === k).length }] : []
  })
  return (
    <div className="grid gap-2">
      {groups.map((g) => (
        <section key={g.kind} aria-label={CHANGELOG_KIND_LABEL[g.kind]}>
          <h3 className="m-0 text-[14px] font-semibold text-text-2">
            {CHANGELOG_KIND_LABEL[g.kind]} · {g.total}
          </h3>
          <ul className="m-0 list-none divide-y divide-border-subtle p-0">
            {g.rows.map((i) => (
              <ChangelogRow key={i.id} item={i} />
            ))}
          </ul>
        </section>
      ))}
      {limit && items.length > limit ? (
        <Button size="sm" variant="ghost" onClick={() => setAll((v) => !v)}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </Button>
      ) : null}
    </div>
  )
}

/**
 * The since-visit baseline for a project (D3, lib/digests.ts `VisitTracker`): fixed while the
 * project is in view, and the stored marker moves when the user leaves after 10 s or more. A
 * return within 15 min continues the same visit.
 */
function useSinceBaseline(projectId: string) {
  const visits = useLastVisits()
  const mark = useMarkVisited()
  const [since, setSince] = useState<{ projectId: string; at: number | undefined } | undefined>()
  const markRef = useRef({ mark, marker: visits.data?.get(projectId) })
  markRef.current = { mark, marker: visits.data?.get(projectId) }
  const ready = visits.isSuccess
  useEffect(() => {
    if (!ready) return
    const at = visitTracker.open(projectId, markRef.current.marker, Date.now())
    setSince({ projectId, at })
    return () => {
      const left = visitTracker.leave(projectId, Date.now())
      if (left !== undefined)
        void markRef.current.mark(projectId, markRef.current.marker, left).catch(() => {})
    }
  }, [projectId, ready])
  return {
    at: since?.projectId === projectId ? since.at : undefined,
    ready: since?.projectId === projectId,
    markSeen: () => {
      const now = Date.now()
      visitTracker.reset(projectId, now)
      setSince({ projectId, at: now })
    },
  }
}

/**
 * "Since you last visited": what changed since this user's marker (synced across devices). The
 * baseline is kept for the visit, so nothing disappears while it is being read.
 */
export function SinceLastVisit({ projectId }: { projectId: string }) {
  const changelogs = useChangelogs()
  const base = useSinceBaseline(projectId)
  if (changelogs.isPending || !base.ready) return null
  const items = itemsSince(changelogs.data?.get(projectId)?.items ?? [], base.at)
  return (
    <section
      className="mx-4 mb-3 rounded-lg border border-border-subtle bg-surface-1 p-3"
      aria-label="Since you last visited"
      data-testid="since-last-visit"
    >
      <div className="mb-1 flex items-baseline gap-2 text-[14px]">
        <span className="font-medium text-text-1">Since you last visited</span>
        <span className="text-text-3">
          {base.at ? <Ago ts={base.at} /> : 'first visit'} · {items.length} change
          {items.length === 1 ? '' : 's'}
        </span>
        <span className="flex-1" />
        {items.length ? (
          <Button size="sm" variant="ghost" onClick={base.markSeen}>
            Mark as seen
          </Button>
        ) : null}
      </div>
      {items.length ? (
        <ChangelogList items={items} limit={6} />
      ) : (
        <p className="m-0 text-[14px] text-text-3">Nothing verified changed since then.</p>
      )}
    </section>
  )
}

/**
 * Compact one-line digest for the page head: counts by type, each opening History › Changes
 * filtered to that type and to the baseline. With nothing new it reads "Nothing changed since …"
 * (hidden after a quick switch away and back, lib/digests.ts `sinceLineState`).
 */
export function SinceLine({ projectId }: { projectId: string }) {
  const changelogs = useChangelogs()
  const base = useSinceBaseline(projectId)
  if (changelogs.isPending || !base.ready) return null
  const items = itemsSince(changelogs.data?.get(projectId)?.items ?? [], base.at)
  const counts = digestCounts(items)
  const state = sinceLineState({ at: base.at, now: Date.now(), changes: counts.length })
  if (state === 'hidden') return null
  const changes = (search: Record<string, unknown>, children: ReactNode, title: string) => (
    <Link
      to="/projects/$projectId"
      params={{ projectId }}
      search={{ tab: 'changes', ...search, ...(base.at ? { since: base.at } : {}) } as never}
      className="lk-since__n"
      title={title}
    >
      {children}
    </Link>
  )
  return (
    <div
      className="lk-since"
      role="group"
      aria-label="Since you last looked"
      data-testid="since-line"
    >
      {state === 'quiet' ? (
        changes(
          {},
          <>
            Nothing changed since <Ago ts={base.at} />
          </>,
          'Open Changes',
        )
      ) : (
        <>
          <span className="lk-since__k">
            {base.at ? (
              <>
                Since you looked <Ago ts={base.at} />:
              </>
            ) : (
              'Since your first visit:'
            )}
          </span>
          {counts.map((c, i) => (
            <span key={c.key}>
              {i ? <span aria-hidden="true"> · </span> : null}
              {changes({ kind: c.kind }, `${c.n} ${c.label}`, `Open Changes: ${c.n} ${c.label}`)}
            </span>
          ))}
        </>
      )}
    </div>
  )
}

/** Count of changes since the user's last visit (project lists, Lanes tabs). */
export function SinceBadge({ projectId }: { projectId: string }) {
  const changelogs = useChangelogs()
  const visits = useLastVisits()
  const n = sinceCounts(changelogs.data, visits.data).get(projectId)
  if (!n) return null
  return (
    <span
      className="vk-chip vk-tone-accent"
      title={`${n} change${n === 1 ? '' : 's'} since you last visited`}
      data-testid="since-badge"
    >
      <span className="vk-chip__text">{n} new</span>
    </span>
  )
}

/** The project's full rolling changelog (two weeks of verified events). */
export function ProjectChangelog({
  projectId,
  compact,
}: {
  projectId: string
  /** A project tab's History: a one-line empty state and no extra padding. */
  compact?: boolean
}) {
  const changelogs = useChangelogs()
  const search = useSearch({ strict: false }) as { kind?: string; since?: number }
  if (changelogs.isPending) return <SkeletonRows />
  const all = changelogs.data?.get(projectId)?.items ?? []
  const items = itemsSince(all, search.since).filter((i) => !search.kind || i.kind === search.kind)
  if (!items.length && compact)
    return (
      <p className="pv-empty">
        {search.kind || search.since
          ? 'No matching changes.'
          : 'Nothing verified yet. Landed work, builds, decisions and failures show here as your machines verify them.'}
      </p>
    )
  if (compact) return <ChangelogList items={items} />
  if (!items.length)
    return (
      <Empty
        title={search.kind || search.since ? 'No matching changes' : 'No verified changes yet'}
      >
        Landed work, builds, releases, decisions, blocks and failures appear here as your machines
        verify them.
      </Empty>
    )
  return (
    <div className="p-4">
      <ChangelogList items={items} />
    </div>
  )
}
